import { toPlainMessage, type PartialMessage } from '@bufbuild/protobuf';

import {
  AnalyticsModel as AnalyticsModelMessage,
  AnalyticsModelSummary as AnalyticsModelSummaryMessage,
  AnalyticsSectionSpec as AnalyticsSectionSpecMessage,
  AnalyticsTagsConfig as AnalyticsTagsConfigMessage,
  CatalogCloneResult as CatalogCloneResultMessage,
  CatalogListing as CatalogListingMessage,
  CatalogStatus,
  ChecklistGenerationStatus,
  CommunityDocumentTemplate as CommunityDocumentTemplateMessage,
  DocumentTemplate as DocumentTemplateMessage,
  DocumentTemplateSummary as DocumentTemplateSummaryMessage,
  EncounterKind as EncounterKindEnum,
  EncounterSchema as EncounterSchemaMessage,
  EncounterSchemaSummary as EncounterSchemaSummaryMessage,
  PartnerTemplateSource as PartnerTemplateSourceMessage,
  RegistryAsset as RegistryAssetMessage,
  RegistryAssetFamily as RegistryAssetFamilyEnum,
  RegistryOriginKind as RegistryOriginKindEnum,
  RegistrySourceKind as RegistrySourceKindEnum,
  SchemaDocumentInclusion,
  SchemaDocumentRef as SchemaDocumentRefMessage,
  TemplateChecklist as TemplateChecklistMessage,
  TemplateClassification as TemplateClassificationMessage,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type {
  AnalyticsModel,
  AnalyticsModelSummary,
  AnalyticsSection,
  AnalyticsTagsConfig,
} from '@/entities/analyticsModel/config';
import type {
  CatalogListing,
  ChecklistGenerationStatus as ChecklistStatus,
  DocumentTemplate,
  DocumentTemplateSummary,
  DocumentType,
  EncounterKind,
  EncounterSchema,
  PartnerTemplateSource,
  SchemaDocumentRef,
  TemplateChecklistResponse,
  TemplateClassification,
} from '@/entities/templates/config';

import type {
  CatalogCloneResult,
  CatalogStatusName,
  CommunityTemplateDetailResponse,
  RegistryAsset,
  RegistryAssetFamily,
  RegistryOriginKind,
  RegistrySourceKind,
} from './scribeCatalogTypes';

const REGISTRY_FAMILY_TO_DOMAIN: Record<
  RegistryAssetFamilyEnum,
  RegistryAssetFamily
> = {
  [RegistryAssetFamilyEnum.RegistryAssetFamilyInvalidValue]:
    'document_template',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyDocumentTemplate]:
    'document_template',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyEncounterSchema]:
    'encounter_schema',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyAnalyticsModel]:
    'analytics_model',
};

const REGISTRY_FAMILY_TO_WIRE: Record<
  RegistryAssetFamily,
  RegistryAssetFamilyEnum
> = {
  document_template:
    RegistryAssetFamilyEnum.RegistryAssetFamilyDocumentTemplate,
  encounter_schema: RegistryAssetFamilyEnum.RegistryAssetFamilyEncounterSchema,
  analytics_model: RegistryAssetFamilyEnum.RegistryAssetFamilyAnalyticsModel,
};

const REGISTRY_SOURCE_KIND_TO_DOMAIN: Record<
  RegistrySourceKindEnum,
  RegistrySourceKind
> = {
  [RegistrySourceKindEnum.RegistrySourceKindInvalidValue]: 'system',
  [RegistrySourceKindEnum.RegistrySourceKindSystem]: 'system',
  [RegistrySourceKindEnum.RegistrySourceKindCommunity]: 'community',
};

const REGISTRY_SOURCE_KIND_TO_WIRE: Record<
  RegistrySourceKind,
  RegistrySourceKindEnum
> = {
  system: RegistrySourceKindEnum.RegistrySourceKindSystem,
  community: RegistrySourceKindEnum.RegistrySourceKindCommunity,
};

const REGISTRY_ORIGIN_KIND_TO_DOMAIN: Record<
  RegistryOriginKindEnum,
  RegistryOriginKind
> = {
  [RegistryOriginKindEnum.RegistryOriginKindInvalidValue]: 'official',
  [RegistryOriginKindEnum.RegistryOriginKindOfficial]: 'official',
  [RegistryOriginKindEnum.RegistryOriginKindCommunity]: 'community',
};

const CHECKLIST_STATUS_TO_DOMAIN: Record<
  ChecklistGenerationStatus,
  ChecklistStatus
> = {
  [ChecklistGenerationStatus.ChecklistGenerationStatusInvalidValue]: 'pending',
  [ChecklistGenerationStatus.ChecklistGenerationStatusPending]: 'pending',
  [ChecklistGenerationStatus.ChecklistGenerationStatusProcessing]: 'processing',
  [ChecklistGenerationStatus.ChecklistGenerationStatusCompleted]: 'completed',
  [ChecklistGenerationStatus.ChecklistGenerationStatusFailed]: 'failed',
};

// scribe stores document types in snake case, the client keeps the screaming
// case its components and form state are written against. Classic content
// reads and writes a stored `letter` as a follow-up letter. Content stamped
// for Transcriptor v2 keeps every stored type, `letter` too.
const DOCUMENT_TYPE_TO_DOMAIN: Record<string, DocumentType> = {
  evaluation: 'EVALUATION',
  summary: 'SUMMARY',
  protocol: 'PROTOCOL',
  literature_review: 'LITERATURE_REVIEW',
  follow_up_letter: 'FOLLOW_UP_LETTER',
  letter: 'FOLLOW_UP_LETTER',
};

const DOCUMENT_TYPE_TO_WIRE: Record<string, string> = {
  letter: 'follow_up_letter',
};

export const documentTypeToWire = (type?: string): string | undefined =>
  type ? DOCUMENT_TYPE_TO_WIRE[type] ?? type.toLowerCase() : undefined;

export const documentTypeToDomain = (
  type: string,
  transcriptorV2 = false,
): DocumentType =>
  transcriptorV2
    ? (type.toUpperCase() as DocumentType)
    : DOCUMENT_TYPE_TO_DOMAIN[type] ?? (type as DocumentType);

// The three statuses scribe stores; the wider client-side VersionStatus adds
// `archived`, which no server response can carry.
export const catalogStatusToDomain = (
  status: CatalogStatus,
): CatalogStatusName => {
  switch (status) {
    case CatalogStatus.CatalogStatusDraft:
      return 'draft';
    case CatalogStatus.CatalogStatusDeleted:
      return 'deleted';
    default:
      return 'active';
  }
};

export const catalogStatusToWire = (
  status?: string,
): CatalogStatus | undefined => {
  switch (status) {
    case 'active':
      return CatalogStatus.CatalogStatusActive;
    case 'draft':
      return CatalogStatus.CatalogStatusDraft;
    case 'deleted':
      return CatalogStatus.CatalogStatusDeleted;
    default:
      return undefined;
  }
};

export const catalogStatusesToWire = (
  statuses?: CatalogStatusName[],
): CatalogStatus[] | undefined =>
  statuses
    ?.map(catalogStatusToWire)
    .filter((status): status is CatalogStatus => status !== undefined);

export const registryFamilyToWire = (
  family?: RegistryAssetFamily,
): RegistryAssetFamilyEnum | undefined =>
  family ? REGISTRY_FAMILY_TO_WIRE[family] : undefined;

export const registrySourceKindToWire = (
  sourceKind?: RegistrySourceKind,
): RegistrySourceKindEnum | undefined =>
  sourceKind ? REGISTRY_SOURCE_KIND_TO_WIRE[sourceKind] : undefined;

const inclusionToDomain = (
  inclusion: SchemaDocumentInclusion,
): SchemaDocumentRef['inclusion'] =>
  inclusion === SchemaDocumentInclusion.SchemaDocumentInclusionOnDemand
    ? 'on_demand'
    : 'always';

const inclusionToWire = (
  inclusion?: SchemaDocumentRef['inclusion'],
): SchemaDocumentInclusion =>
  inclusion === 'on_demand'
    ? SchemaDocumentInclusion.SchemaDocumentInclusionOnDemand
    : SchemaDocumentInclusion.SchemaDocumentInclusionAlways;

const checklistToDomain = (
  checklist: TemplateChecklistMessage,
): TemplateChecklistResponse => ({
  status: CHECKLIST_STATUS_TO_DOMAIN[checklist.Status] ?? 'pending',
  error: checklist.Error,
  content: checklist.Content && {
    items: checklist.Content.Items,
    generated_at: checklist.Content.GeneratedAt?.toDate().toISOString() ?? '',
    model: checklist.Content.Model,
  },
});

const catalogListingToDomain = (
  listing: CatalogListingMessage,
): CatalogListing => ({
  specialty: listing.Specialty,
  category: listing.Category,
  tags: listing.Tags,
  import_count: listing.ImportCount,
  description: listing.Description,
  location: listing.Location,
  published_at: listing.PublishedAt?.toDate().toISOString(),
});

const classificationToDomain = (
  classification?: TemplateClassificationMessage,
): TemplateClassification | undefined =>
  classification && {
    department: classification.Department,
    group: classification.Group,
  };

const partnerSourceToDomain = (
  source?: PartnerTemplateSourceMessage,
): PartnerTemplateSource | undefined =>
  source && {
    partner: source.Partner,
    external_id: source.ExternalID,
    format: source.Format,
    category: source.Category,
  };

export const documentTemplateSummaryToDomain = (
  template: DocumentTemplateSummaryMessage,
): DocumentTemplateSummary => ({
  id: template.ID,
  transcriptor_v2: template.TranscriptorV2 === true,
  series_id: template.SeriesID,
  code: template.Code,
  language: template.Language,
  version: template.Version,
  status: catalogStatusToDomain(template.Status),
  organization_id: template.OrganizationID,
  source_kind: template.SourceKind as DocumentTemplateSummary['source_kind'],
  derived_from_kind:
    template.DerivedFromKind as DocumentTemplateSummary['derived_from_kind'],
  derived_from_id: template.DerivedFromID,
  type: documentTypeToDomain(template.Type, template.TranscriptorV2 === true),
  name: template.Name,
  tab_title: template.TabTitle,
  description: template.Description,
  sections_count: template.SectionsCount,
  catalog_published: template.CatalogPublished,
  created_at: template.CreatedAt?.toDate().toISOString() ?? '',
  updated_at: template.UpdatedAt?.toDate().toISOString(),
  format: template.Format,
  classification: classificationToDomain(template.Classification),
  partner_source: partnerSourceToDomain(template.PartnerSource),
  edited_by_clinic: template.EditedByClinic,
  added_by_clinic: template.AddedByClinic,
  newer_community_template_id: template.NewerCommunityTemplateID,
});

export const documentTemplateToDomain = (
  template: DocumentTemplateMessage = new DocumentTemplateMessage(),
): DocumentTemplate => ({
  id: template.ID,
  transcriptor_v2: template.TranscriptorV2 === true,
  series_id: template.SeriesID,
  code: template.Code,
  language: template.Language,
  version: template.Version,
  status: catalogStatusToDomain(template.Status),
  is_system: template.IsSystem,
  organization_id: template.OrganizationID,
  source_kind: template.SourceKind as DocumentTemplate['source_kind'],
  derived_from_kind:
    template.DerivedFromKind as DocumentTemplate['derived_from_kind'],
  derived_from_id: template.DerivedFromID,
  type: documentTypeToDomain(template.Type, template.TranscriptorV2 === true),
  name: template.Name,
  tab_title: template.TabTitle,
  description: template.Description,
  sections: template.Sections.map((section) => toPlainMessage(section)),
  catalog_published: template.CatalogPublished,
  catalog_metadata:
    template.CatalogListing && catalogListingToDomain(template.CatalogListing),
  checklist: template.Checklist && checklistToDomain(template.Checklist),
  source_document: template.SourceDocument,
  created_at: template.CreatedAt?.toDate().toISOString() ?? '',
  updated_at: template.UpdatedAt?.toDate().toISOString(),
  format: template.Format,
  classification: classificationToDomain(template.Classification),
  partner_source: partnerSourceToDomain(template.PartnerSource),
  edited_by_clinic: template.EditedByClinic,
  added_by_clinic: template.AddedByClinic,
});

const schemaDocumentRefToDomain = (
  document: SchemaDocumentRefMessage,
  transcriptorV2: boolean,
): SchemaDocumentRef => ({
  template_series_id: document.TemplateSeriesID,
  code: document.Code ?? '',
  inclusion: inclusionToDomain(document.Inclusion),
  name: document.Name,
  tab_title: document.TabTitle,
  type: document.Type
    ? documentTypeToDomain(document.Type, transcriptorV2)
    : undefined,
});

export const schemaDocumentRefToWire = (
  document: Pick<SchemaDocumentRef, 'template_series_id' | 'inclusion'>,
): PartialMessage<SchemaDocumentRefMessage> => ({
  TemplateSeriesID: document.template_series_id ?? '',
  Inclusion: inclusionToWire(document.inclusion),
});

// An unset kind stays undefined so the workspace can tell a stored kind from
// its document-type fallback.
const ENCOUNTER_KIND_TO_DOMAIN: Partial<
  Record<EncounterKindEnum, EncounterKind>
> = {
  [EncounterKindEnum.EncounterKindConsultation]: 'consultation',
  [EncounterKindEnum.EncounterKindTreatment]: 'treatment',
};

export const encounterSchemaSummaryToDomain = (
  schema: EncounterSchemaSummaryMessage,
): EncounterSchema => ({
  id: schema.ID,
  transcriptor_v2: schema.TranscriptorV2 === true,
  code: schema.Code,
  language: schema.Language,
  status: catalogStatusToDomain(schema.Status),
  organization_id: schema.OrganizationID,
  source_kind: schema.SourceKind as EncounterSchema['source_kind'],
  derived_from_kind:
    schema.DerivedFromKind as EncounterSchema['derived_from_kind'],
  derived_from_id: schema.DerivedFromID,
  name: schema.Name,
  description: schema.Description,
  kind: ENCOUNTER_KIND_TO_DOMAIN[schema.Kind],
  analytics_model_series_id: schema.AnalyticsModelSeriesID,
  documents: schema.Documents.map((document) =>
    schemaDocumentRefToDomain(document, schema.TranscriptorV2 === true),
  ),
  document_count: schema.DocumentCount,
  created_at: schema.CreatedAt?.toDate().toISOString(),
});

export const encounterSchemaToDomain = (
  schema: EncounterSchemaMessage = new EncounterSchemaMessage(),
): EncounterSchema => ({
  id: schema.ID,
  transcriptor_v2: schema.TranscriptorV2 === true,
  code: schema.Code,
  language: schema.Language,
  status: catalogStatusToDomain(schema.Status),
  is_system: schema.IsSystem,
  organization_id: schema.OrganizationID,
  source_kind: schema.SourceKind as EncounterSchema['source_kind'],
  derived_from_kind:
    schema.DerivedFromKind as EncounterSchema['derived_from_kind'],
  derived_from_id: schema.DerivedFromID,
  name: schema.Name,
  description: schema.Description,
  kind: ENCOUNTER_KIND_TO_DOMAIN[schema.Kind],
  analytics_model_series_id: schema.AnalyticsModelSeriesID,
  documents: schema.Documents.map((document) =>
    schemaDocumentRefToDomain(document, schema.TranscriptorV2 === true),
  ),
  always_document_codes: schema.AlwaysDocumentCodes,
  on_demand_document_codes: schema.OnDemandDocumentCodes,
  always_document_series_ids: schema.AlwaysDocumentSeriesIDs,
  on_demand_document_series_ids: schema.OnDemandDocumentSeriesIDs,
  document_count: schema.DocumentCount,
  created_at: schema.CreatedAt?.toDate().toISOString(),
  created_by: schema.CreatedBy,
});

const analyticsTagsToDomain = (
  tags?: AnalyticsTagsConfigMessage,
): AnalyticsTagsConfig => ({
  instructions: tags?.Instructions,
  groups: (tags?.Groups ?? []).map((group) => ({
    code: group.Code,
    name: group.Name,
    type: group.Type as AnalyticsTagsConfig['groups'][number]['type'],
    instructions: group.Instructions,
    selection_mode:
      group.SelectionMode as AnalyticsTagsConfig['groups'][number]['selection_mode'],
    required: group.Required,
    tags: group.Tags.map((tag) => ({
      code: tag.Code,
      name: tag.Name,
      instructions: tag.Instructions,
      explain: tag.Explain,
    })),
  })),
});

export const analyticsTagsToWire = (
  tags?: AnalyticsTagsConfig,
): PartialMessage<AnalyticsTagsConfigMessage> | undefined =>
  tags && {
    Instructions: tags.instructions ?? undefined,
    Groups: tags.groups.map((group) => ({
      Code: group.code,
      Name: group.name,
      Type: group.type,
      Instructions: group.instructions,
      SelectionMode: group.selection_mode,
      Required: group.required,
      Tags: group.tags.map((tag) => ({
        Code: tag.code,
        Name: tag.name,
        Instructions: tag.instructions,
        Explain: tag.explain,
      })),
    })),
  };

const analyticsSectionToDomain = (
  section: AnalyticsSectionSpecMessage,
): AnalyticsSection => ({
  code: section.Code,
  name: section.Name,
  kind: section.Kind as AnalyticsSection['kind'],
  instructions: section.Instructions,
});

export const analyticsSectionToWire = (
  section: AnalyticsSection,
): PartialMessage<AnalyticsSectionSpecMessage> => ({
  Code: section.code,
  Name: section.name,
  Kind: section.kind,
  Instructions: section.instructions,
});

export const analyticsModelSummaryToDomain = (
  model: AnalyticsModelSummaryMessage,
): AnalyticsModelSummary => ({
  id: model.ID,
  series_id: model.SeriesID,
  code: model.Code,
  language: model.Language,
  version: model.Version,
  status: catalogStatusToDomain(model.Status),
  organization_id: model.OrganizationID,
  source_kind: model.SourceKind as AnalyticsModel['source_kind'],
  derived_from_kind:
    model.DerivedFromKind as AnalyticsModel['derived_from_kind'],
  derived_from_id: model.DerivedFromID,
  name: model.Name,
  created_at: model.CreatedAt?.toDate().toISOString() ?? '',
});

export const analyticsModelToDomain = (
  model: AnalyticsModelMessage = new AnalyticsModelMessage(),
): AnalyticsModel => ({
  id: model.ID,
  series_id: model.SeriesID,
  code: model.Code,
  language: model.Language,
  version: model.Version,
  status: catalogStatusToDomain(model.Status),
  is_system: model.IsSystem,
  organization_id: model.OrganizationID,
  source_kind: model.SourceKind as AnalyticsModel['source_kind'],
  derived_from_kind:
    model.DerivedFromKind as AnalyticsModel['derived_from_kind'],
  derived_from_id: model.DerivedFromID,
  name: model.Name,
  tags: analyticsTagsToDomain(model.Tags),
  sections: model.Sections.map(analyticsSectionToDomain),
  created_at: model.CreatedAt?.toDate().toISOString() ?? '',
});

export const communityTemplateToDomain = (
  template: CommunityDocumentTemplateMessage = new CommunityDocumentTemplateMessage(),
): CommunityTemplateDetailResponse => ({
  id: template.ID,
  code: template.Code,
  name: template.Name,
  description: template.Description,
  type: template.Type,
  language: template.Language,
  tab_title: template.TabTitle,
  specialty: template.Specialty,
  category: template.Category,
  location: template.Location,
  tags: template.Tags,
  author_display_name: template.AuthorDisplayName,
  author_specialty: template.AuthorSpecialty,
  source_template_id: template.SourceTemplateID,
  source_series_id: template.SourceSeriesID,
  source_organization_id: template.SourceOrganizationID,
  sections: template.Sections.map((section) => toPlainMessage(section)),
  import_count: template.ImportCount,
  sections_count: template.SectionsCount,
  published_at: template.PublishedAt?.toDate().toISOString(),
});

export const registryAssetToDomain = (
  asset: RegistryAssetMessage,
): RegistryAsset => ({
  id: asset.ID,
  family: REGISTRY_FAMILY_TO_DOMAIN[asset.Family],
  sourceKind: REGISTRY_SOURCE_KIND_TO_DOMAIN[asset.SourceKind],
  originKind: REGISTRY_ORIGIN_KIND_TO_DOMAIN[asset.OriginKind],
  language: asset.Language,
  name: asset.Name,
  description: asset.Description ?? null,
  templateType: (asset.TemplateType ?? null) as RegistryAsset['templateType'],
  sectionsCount: asset.SectionsCount ?? null,
  documentCount: asset.DocumentCount ?? null,
  analyticsGroupsCount: asset.AnalyticsGroupsCount ?? null,
  analyticsSectionsCount: asset.AnalyticsSectionsCount ?? null,
  authorDisplayName: asset.AuthorDisplayName ?? null,
  authorSpecialty: asset.AuthorSpecialty ?? null,
  specialty: asset.Specialty ?? null,
  category: asset.Category ?? null,
  importCount: asset.ImportCount ?? null,
  hasLocalClone: asset.HasLocalClone,
  localCloneId: asset.LocalCloneID ?? null,
  classification: classificationToDomain(asset.Classification) ?? null,
});

export const cloneResultToDomain = (
  result?: CatalogCloneResultMessage,
): CatalogCloneResult => ({
  local_asset_id: result?.LocalAssetID ?? '',
  local_series_id: result?.LocalSeriesID,
  reused_existing: result?.ReusedExisting ?? false,
});
